// Generic evaluator for the `conditions` array GET .../flow-statuses now
// attaches to every manual_actions entry — e.g.
//   { key: "acknowledge_interview", conditions: [
//       { field: "interview_details.requires_acknowledgment", op: "eq", value: true }
//   ]}
// An action is allowed for a given candidate row only when ALL of its own
// conditions pass against that row's data (empty array — the common case —
// always passes). Nothing here is senior_level_flow-specific: it only reads
// `action.conditions` and dot-paths into whatever row object it's given, so
// it works unmodified the moment any other flow's manual_actions gain
// conditions too.

// Resolves a dot-path ("interview_details.requires_acknowledgment") against
// an object, the way lodash's get() would — this codebase has no lodash
// dependency and every path seen so far is shallow, so a tiny local
// resolver beats pulling in a library for one function.
const resolvePath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

// "$now" is the one dynamic value the backend sends — resolved fresh at
// evaluation time (not once when the flow-statuses response loaded) so a
// check like "interview_time < $now" stays live as the clock moves.
const resolveConditionValue = (value) =>
  value === '$now' ? new Date() : value;

// Both sides normalized to a Date when possible; null when not — used by
// the ordering operators, which only ever compare date-ish fields
// (interview_time, meeting_ended_at) in the responses seen so far.
const asDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

// A condition with `value: null` (e.g. "meeting_ended_at" neq null) should
// also catch a genuinely absent field — a candidate whose interview hasn't
// happened yet carries no meeting_ended_at key at all, not a literal null —
// so undefined and null are treated as the same "not set" value for eq/neq.
const normalizeNullish = (value) => (value === undefined ? null : value);

const OPERATORS = {
  eq: (actual, expected) =>
    normalizeNullish(actual) === normalizeNullish(expected),
  neq: (actual, expected) =>
    normalizeNullish(actual) !== normalizeNullish(expected),
  lt: (actual, expected) => {
    const a = asDate(actual);
    const b = asDate(expected);
    return !!a && !!b && a.getTime() < b.getTime();
  },
  lte: (actual, expected) => {
    const a = asDate(actual);
    const b = asDate(expected);
    return !!a && !!b && a.getTime() <= b.getTime();
  },
  gt: (actual, expected) => {
    const a = asDate(actual);
    const b = asDate(expected);
    return !!a && !!b && a.getTime() > b.getTime();
  },
  gte: (actual, expected) => {
    const a = asDate(actual);
    const b = asDate(expected);
    return !!a && !!b && a.getTime() >= b.getTime();
  },
};

const evaluateOneCondition = (condition, row) => {
  const { field, op } = condition || {};
  const comparator = op && OPERATORS[op];
  // Fail closed: a condition this evaluator can't understand (missing
  // field/op, or an operator the backend added before the frontend knew
  // about it) hides the action rather than risking one the backend would
  // reject — worst case a TA doesn't see an action they could technically
  // use, until this list grows an operator to match.
  if (!field || !comparator) return false;
  return comparator(
    resolvePath(row, field),
    resolveConditionValue(condition.value)
  );
};

export const evaluateConditions = (conditions, row) =>
  (conditions || []).every((condition) => evaluateOneCondition(condition, row));

// Convenience for the common case: narrow a manual_actions list down to
// what one specific row is currently allowed to do.
export const filterActionsForRow = (actions, row) =>
  (actions || []).filter((action) =>
    evaluateConditions(action.conditions, row)
  );

// Bulk-bar version: an action only belongs in a multi-select action bar
// when EVERY currently selected row satisfies its conditions — firing it
// against a row that doesn't qualify would just 400. No rows selected means
// nothing has been checked yet, so nothing passes.
export const filterActionsForRows = (actions, rows) =>
  (actions || []).filter(
    (action) =>
      rows.length > 0 &&
      rows.every((row) => evaluateConditions(action.conditions, row))
  );
